import type { EventPreview } from '../pages/EventInvitePage';
import type { FeatureIconName } from '../components/FeatureIcon';

export type CharacteristicTone = 'neutral' | 'info' | 'success' | 'warning';
export interface CharacteristicChip {
  id: string;
  label: string;
  icon: FeatureIconName;
  tone: CharacteristicTone;
}
export interface EventRequirement {
  group: 'Experience' | 'Who can join' | 'Motorcycle';
  label: string;
  value: string;
}

// Canonical RidingStyleValue (CreateEventTypes.swift) — ordered for range checks.
const PACE_ORDER = ['leisure', 'casual', 'spirited', 'aggressive'];
const PACE_LABELS: Record<string, string> = {
  leisure: 'Leisure',
  casual: 'Casual',
  spirited: 'Spirited',
  aggressive: 'Aggressive',
};
// legacyRidingStyle supports casual/spirited/aggressive only — no leisure.
const LEGACY_PACE = new Set(['casual', 'spirited', 'aggressive']);

// Canonical EventRouteType display labels.
const ROUTE_LABELS: Record<string, string> = {
  urban: 'City Streets',
  city_and_highway: 'Mixed Roads',
  rural: 'Backroads',
  twisties: 'Winding Roads',
};

// ParticipantExperience — beginner is "All welcome", not an exclusion rule.
const EXPERIENCE_LABELS: Record<string, string> = {
  beginner: 'All welcome',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
  expert: 'Expert',
};

const VEHICLE_LABELS: Record<string, string> = {
  sport: 'Sport',
  cruiser: 'Cruiser',
  touring: 'Touring',
  adventure: 'Adventure',
  offroad: 'Off-Road',
  standard: 'Standard',
  custom: 'Custom',
  scooter: 'Scooter',
  electric: 'Electric',
  three_wheeler: 'Three-Wheeler',
  other: 'Other',
};

// Canonical MotorcycleCategory display names.
const CATEGORY_LABELS: Record<string, string> = {
  sport: 'Sport Bike',
  cruiser: 'Cruiser',
  touring: 'Touring',
  adventure: 'Adventure',
  naked: 'Naked',
  'dual-sport': 'Dual Sport',
  'dirt-bike': 'Dirt Bike',
  scooter: 'Scooter',
  electric: 'Electric',
  'three-wheeler': 'Three-Wheeler',
  standard: 'Standard',
};

const COLOR_LABELS: Record<string, string> = {
  black: 'Black',
  white: 'White',
  red: 'Red',
  blue: 'Blue',
  silver: 'Silver',
  gray: 'Gray',
  green: 'Green',
  yellow: 'Yellow',
  orange: 'Orange',
  purple: 'Purple',
  brown: 'Brown',
};

const norm = (v: unknown): string =>
  typeof v === 'string' ? v.trim().toLowerCase() : '';

// Prototype keys ('__proto__', 'constructor', 'toString') must never resolve to
// a "known" label — own-property checks only.
const has = (map: Record<string, string>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(map, key);

const hasControlChar = (s: string): boolean => {
  for (const ch of s) {
    if (ch.charCodeAt(0) < 32) return true;
  }
  return false;
};

const stringList = (v: unknown): string[] => {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const item of v) {
    if (typeof item !== 'string') continue;
    const trimmed = item.trim();
    if (trimmed && !hasControlChar(trimmed) && !out.includes(trimmed)) out.push(trimmed);
  }
  return out;
};

const ccBound = (v: unknown): number | null =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : null;

const paceChip = (event: EventPreview): CharacteristicChip | null => {
  // ANY provided bound (wrong type, empty string included) must validate as a
  // known ordered pair — a malformed bound never falls back to legacy style.
  const boundsProvided = event.ridingStyleMin != null || event.ridingStyleMax != null;
  if (boundsProvided) {
    const min = norm(event.ridingStyleMin);
    const max = norm(event.ridingStyleMax);
    if (!has(PACE_LABELS, min) || !has(PACE_LABELS, max)) return null;
    if (PACE_ORDER.indexOf(min) > PACE_ORDER.indexOf(max)) return null;
    const label = min === max ? PACE_LABELS[min] : `${PACE_LABELS[min]} to ${PACE_LABELS[max]}`;
    return { id: 'pace', label, icon: 'gauge', tone: 'neutral' };
  }
  const legacy = norm(event.ridingStyle);
  return legacy && LEGACY_PACE.has(legacy)
    ? { id: 'pace', label: PACE_LABELS[legacy], icon: 'gauge', tone: 'neutral' }
    : null;
};

const mapList = (v: unknown, labels: Record<string, string>): string[] => {
  const seen: string[] = [];
  for (const raw of stringList(v)) {
    const key = norm(raw);
    if (has(labels, key) && !seen.includes(labels[key])) seen.push(labels[key]);
  }
  return seen;
};

const ccRule = (minRaw: unknown, maxRaw: unknown): string | null => {
  const min = ccBound(minRaw);
  const max = ccBound(maxRaw);
  if (minRaw == null && maxRaw == null) return null;
  if (min !== null && max !== null) {
    if (min === max) return `Exactly ${min} cc`;
    return min < max ? `${min}\u2013${max} cc` : null;
  }
  if (min !== null && maxRaw == null) return `At least ${min} cc`;
  if (max !== null && minRaw == null) return `Up to ${max} cc`;
  return null;
};

export function buildEventCharacteristics(event: EventPreview): {
  chips: CharacteristicChip[];
  requirements: EventRequirement[];
} {
  const chips: CharacteristicChip[] = [];
  const requirements: EventRequirement[] = [];
  const restrictions =
    event.restrictions && typeof event.restrictions === 'object' ? event.restrictions : null;

  const pace = paceChip(event);
  if (pace) chips.push(pace);

  if (typeof event.routeType === 'string' && event.routeType.trim()) {
    const seen = new Set<string>();
    for (const token of event.routeType.split(',')) {
      const key = token.trim().toLowerCase();
      if (has(ROUTE_LABELS, key) && !seen.has(key)) {
        seen.add(key);
        chips.push({ id: `route-${key}`, label: ROUTE_LABELS[key], icon: 'route', tone: 'neutral' });
      }
    }
  }

  const lane = norm(event.laneFiltering);
  if (lane === 'yes') {
    chips.push({ id: 'lane-filtering', label: 'Lane filtering', icon: 'road', tone: 'neutral' });
  } else if (lane === 'no') {
    chips.push({ id: 'lane-filtering', label: 'No lane filtering', icon: 'road', tone: 'neutral' });
  }

  const moto: EventRequirement[] = [];
  if (restrictions) {
    const types = mapList(restrictions.allowedTypes, CATEGORY_LABELS);
    const colors = mapList(restrictions.allowedColors, COLOR_LABELS);
    const makes = stringList(restrictions.allowedMakes);
    const models = stringList(restrictions.allowedModels);
    const excludedMakes = stringList(restrictions.excludedMakes);
    const excludedModels = stringList(restrictions.excludedModels);
    const cc = ccRule(restrictions.minEngineCC, restrictions.maxEngineCC);

    // "or" within a category = alternatives; every configured category applies.
    if (types.length) moto.push({ group: 'Motorcycle', label: 'Type', value: types.join(' or ') });
    if (makes.length) moto.push({ group: 'Motorcycle', label: 'Make', value: makes.join(' or ') });
    if (models.length) moto.push({ group: 'Motorcycle', label: 'Model', value: models.join(' or ') });
    if (colors.length) moto.push({ group: 'Motorcycle', label: 'Color', value: colors.join(' or ') });
    if (cc) moto.push({ group: 'Motorcycle', label: 'Engine size', value: cc });
    if (excludedMakes.length) {
      moto.push({ group: 'Motorcycle', label: 'Excluded makes', value: excludedMakes.join(', ') });
    }
    if (excludedModels.length) {
      moto.push({ group: 'Motorcycle', label: 'Excluded models', value: excludedModels.join(', ') });
    }
  }

  // A stored vehicle type chip only when it isn't already covered by the
  // motorcycle type criteria.
  const vehicle = norm(event.vehicleType);
  const vehicleLabel = has(VEHICLE_LABELS, vehicle) ? VEHICLE_LABELS[vehicle] : undefined;
  const hasAllowedTypes = moto.some((r) => r.label === 'Type');
  if (vehicleLabel && !hasAllowedTypes) {
    chips.push({ id: 'vehicle-type', label: vehicleLabel, icon: 'wrench', tone: 'neutral' });
  }

  const experience = norm(event.skillLevel);
  const experienceLabel = has(EXPERIENCE_LABELS, experience) ? EXPERIENCE_LABELS[experience] : undefined;
  if (experience === 'beginner') {
    chips.push({ id: 'experience', label: 'All welcome', icon: 'users', tone: 'success' });
  } else if (experienceLabel) {
    requirements.push({ group: 'Experience', label: 'Experience', value: `${experienceLabel} & up` });
  }

  const gender = norm(event.allowedGender);
  if (gender === 'male') {
    requirements.push({ group: 'Who can join', label: 'Ride', value: 'Men\u2019s ride' });
  } else if (gender === 'female') {
    requirements.push({ group: 'Who can join', label: 'Ride', value: 'Women\u2019s ride' });
  }

  if (event.carsWelcome === true) {
    chips.push({ id: 'cars-welcome', label: 'Cars welcome', icon: 'users', tone: 'neutral' });
  }

  if (moto.length) requirements.push(...moto);

  return { chips, requirements };
}
